import { TransformationStepType, WorkflowStepType } from "@/constants/workflows/workflowCreate";
import { TransformationStep } from "@/types/WorkflowCreate/workflowCreate";
import { WorkflowInDetailV2 } from "@/types/WorkflowCreate/workflowDetailsV2";
import { WorkflowTask } from "@/types/WorkflowCreate/workflowUpdateV2";

type TransformationOperation = "create" | "update" | "delete";

type ComposeParams = {
  draft?: TransformationStep;
  fallback?: TransformationStep;
  operation: TransformationOperation;
  taskOrder: number;
  taskId?: string;
};

const getWorkflowTaskIdsByOrder = (workflowTasks: WorkflowTask[]): Map<number, string | undefined> => {
  const map = new Map<number, string | undefined>();
  workflowTasks.forEach(task => {
    if (typeof task.order === "number") {
      map.set(task.order, task.id);
    }
  });
  return map;
};

const flattenExistingTransformationSteps = (
  workflowTasks: WorkflowInDetailV2["workflowTasks"]
): TransformationStep[] => {
  return workflowTasks
    .filter(task => task.stepType === WorkflowStepType.TRANSFORMATION)
    .flatMap(task => (task.transformationSteps ?? []).map((step, index) => ({
      configuration: step.configuration,
      taskOrder: task.order,
      stepName: step.stepName,
      stepType: step.stepType,
      order: (step as unknown as { order?: number }).order ?? index + 1,
      stepId: step.id,
      id: step.id,
      taskId: step.workflowTaskId ?? task.id,
      isActive: step.isEnabled,
      isEnabled: step.isEnabled,
      validationRules: step.validationRules,
      operation: "update"
    })));
};

const groupByTaskOrder = (steps: TransformationStep[]): Map<number, TransformationStep[]> => {
  const groups = new Map<number, TransformationStep[]>();

  steps.forEach(step => {
    if (typeof step.taskOrder !== "number") return;
    const list = groups.get(step.taskOrder);
    if (list) {
      list.push(step);
    } else {
      groups.set(step.taskOrder, [step]);
    }
  });

  return groups;
};

const buildTransformationPayload = ({
  draft,
  fallback,
  operation,
  taskOrder,
  taskId
}: ComposeParams): TransformationStep => {
  const resolvedStepType = draft?.stepType ?? fallback?.stepType ?? TransformationStepType.MAP_OBJECTS;
  const resolvedStepName = draft?.stepName ?? fallback?.stepName ?? String(resolvedStepType);
  const resolvedConfig = draft?.configuration ?? fallback?.configuration ?? {};
  const resolvedOrder = draft?.order ?? fallback?.order ?? taskOrder;
  const resolvedTaskId = draft?.taskId ?? fallback?.taskId ?? taskId;
  const resolvedStepId = draft?.stepId ?? fallback?.stepId;

  const payload: TransformationStep = {
    configuration: resolvedConfig,
    taskOrder,
    stepName: resolvedStepName,
    stepType: resolvedStepType,
    order: resolvedOrder,
    description: draft?.description ?? fallback?.description,
    templateId: draft?.templateId ?? fallback?.templateId,
    operation,
    isActive: draft?.isActive ?? fallback?.isActive,
    isEnabled: draft?.isEnabled ?? fallback?.isEnabled,
    validationRules: draft?.validationRules ?? fallback?.validationRules,
    taskId: resolvedTaskId,
    stepId: resolvedStepId,
    id: draft?.id ?? fallback?.id,
  };

  if (operation === "delete" || draft?.remove) {
    payload.remove = true;
  }

  return payload;
};

export const getTransformationsDifference = (
  workflowTasks: WorkflowInDetailV2["workflowTasks"],
  draftTransformations: TransformationStep[],
  updatedWorkflowTasks: WorkflowTask[]
): TransformationStep[] => {
  const existingGroups = groupByTaskOrder(flattenExistingTransformationSteps(workflowTasks));
  const draftGroups = groupByTaskOrder(draftTransformations);
  const workflowTaskIds = getWorkflowTaskIdsByOrder(updatedWorkflowTasks);
  const results: TransformationStep[] = [];

  draftGroups.forEach((draftGroup, taskOrder) => {
    const existingGroup = existingGroups.get(taskOrder) ?? [];
    const remainingExisting = [...existingGroup];

    draftGroup.forEach(draft => {
      let matchedExisting: TransformationStep | undefined;

      if (draft.stepId) {
        const matchIndex = remainingExisting.findIndex(step => step.stepId === draft.stepId);
        if (matchIndex >= 0) {
          matchedExisting = remainingExisting.splice(matchIndex, 1)[0];
        }
      }

      if (!matchedExisting) {
        matchedExisting = remainingExisting.shift();
      }

      if (draft.remove) {
        results.push(
          buildTransformationPayload({
            draft,
            fallback: matchedExisting ?? draft,
            operation: "delete",
            taskOrder,
            taskId: matchedExisting?.taskId ?? draft.taskId ?? workflowTaskIds.get(taskOrder)
          })
        );
        return;
      }

      const operation: TransformationOperation = matchedExisting ? "update" : "create";

      results.push(
        buildTransformationPayload({
          draft,
          fallback: matchedExisting,
          operation,
          taskOrder,
          taskId: matchedExisting?.taskId ?? draft.taskId ?? workflowTaskIds.get(taskOrder)
        })
      );
    });

    remainingExisting.forEach(existingStep => {
      results.push(
        buildTransformationPayload({
          fallback: existingStep,
          operation: "delete",
          taskOrder,
          taskId: existingStep.taskId ?? workflowTaskIds.get(taskOrder)
        })
      );
    });

    existingGroups.delete(taskOrder);
  });

  existingGroups.forEach((existingGroup, taskOrder) => {
    existingGroup.forEach(existingStep => {
      results.push(
        buildTransformationPayload({
          fallback: existingStep,
          operation: "delete",
          taskOrder,
          taskId: existingStep.taskId ?? workflowTaskIds.get(taskOrder)
        })
      );
    });
  });

  return results;
};
